import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { Pagination, Spinner } from '~/ui';
import { useState, useEffect, useCallback } from 'react';
import TrashIcon from '~/assets/svg/newSvg/basket.svg?react';
import EmptyImageIcon from '~/assets/svg/newSvg/empty-image.svg?react';
import ArrowLeftIcon from '~/assets/svg/newSvg/arrow-light-left.svg?react';
import ArrowRightIcon from '~/assets/svg/newSvg/arrow-light-right.svg?react';
import { Modal } from '~/components/Shared/Modal/Modal';
import { Portal } from '~/components/Shared/Portal/Portal';
import CloseIcon from '~/assets/svg/newSvg/cross-close.svg?react';
import styles from './PhotoStoragePhotos.module.scss';

interface IPhoto {
  id: number;
  photo_id: number;
  asset_id: number;
  module: string;
  url: string;
}

const PhotoStoragePhotos = () => {
  const { t } = useTranslation();
  const { module, assetId } = useParams<{ module: string; assetId: string }>();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();
  const [page, setPage] = useState(1);
  const [displayedPhoto, setDisplayedPhoto] = useState<IPhoto | null>(null);
  const [incomingPhoto, setIncomingPhoto] = useState<IPhoto | null>(null);
  const [previewBroken, setPreviewBroken] = useState(false);
  const [navDir, setNavDir] = useState<'left' | 'right'>('right');
  const [animKey, setAnimKey] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [loadedIds, setLoadedIds] = useState<Set<number>>(new Set());

  const handlePageChange = (selectedPage: { selected: number }) => {
    setPage(selectedPage.selected + 1);
    setLoadedIds(new Set());
  };

  const moduleName = t(`photo_storage_${module}`);

  const { data: photos, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}`,
        { params: { perPage: 20, page } }
      );
      return data;
    },
    queryKey: ['photo-storage-photos', module, assetId, page],
    onError: error => toastError(error),
  });

  const photoList: IPhoto[] = photos?.data || [];

  const openPreview = (photo: IPhoto) => {
    setDisplayedPhoto(photo);
    setIncomingPhoto(null);
    setPreviewBroken(false);
    setAnimKey(k => k + 1);
  };

  const navigate = useCallback((dir: 1 | -1) => {
    if (!photoList.length) return;
    const base = incomingPhoto || displayedPhoto;
    if (!base) return;
    const idx = photoList.findIndex(p => p.id === base.id);
    const next = photoList[(idx + dir + photoList.length) % photoList.length];
    setNavDir(dir === 1 ? 'right' : 'left');
    setPreviewBroken(false);
    setIncomingPhoto(next);
  }, [displayedPhoto, incomingPhoto, photoList]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!displayedPhoto) return;
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayedPhoto, navigate]);

  const { mutateAsync: deletePhoto } = useMutation(
    async (id: number) => {
      await instance.delete(`admin/photo-storage/${id}`);
    },
    {
      onSuccess: () => {
        toastSuccess(t('deleted_successfully'));
        client.invalidateQueries(['photo-storage-photos']);
        client.invalidateQueries(['photo-storage-assets']);
        client.invalidateQueries(['photo-storage-modules']);
        setDeleteConfirmId(null);
      },
      onError: error => toastError(error),
    }
  );

  return (
    <Page heading={<Heading text={`${t('photo_storage')} — ${moduleName}`} />}>
      <div className={styles.container}>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className={styles.grid}>
              {(photos?.data || []).map((photo: IPhoto) => (
                <div key={photo.id} className={styles.photoCard}>
                  {!loadedIds.has(photo.id) && (
                    <div className={styles.shimmer} />
                  )}
                  <button
                    type="button"
                    className={styles.photoButton}
                    onClick={() => openPreview(photo)}
                  >
                    <img
                      src={photo.url}
                      alt=""
                      className={styles.photoImage}
                      onLoad={() =>
                        setLoadedIds(prev => new Set(prev).add(photo.id))
                      }
                      onError={e => {
                        setLoadedIds(prev => new Set(prev).add(photo.id));
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.add(styles.brokenVisible);
                      }}
                    />
                    <div className={styles.brokenPlaceholder}>
                      <EmptyImageIcon className={styles.brokenIcon} />
                      <span className={styles.brokenText}>{t('failed_to_load')}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => setDeleteConfirmId(photo.id)}
                  >
                    <TrashIcon className={styles.deleteIcon} />
                  </button>
                </div>
              ))}
              {!photos?.data?.length && (
                <p className={styles.empty}>{t('no_data')}</p>
              )}
            </div>
            <div className={styles.paginationWrapper}>
              <Pagination
                pageCount={photos?.last_page || 1}
                forcePage={page}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>

      {/* Просмотр фото — fullscreen */}
      {displayedPhoto && (
        <Portal>
          <div className={styles.lightbox} onClick={() => setDisplayedPhoto(null)}>
            {/* Размытый фон — обновляется только когда incoming загружен */}
            <div
              className={styles.lightboxBg}
              style={{ backgroundImage: `url(${displayedPhoto.url})` }}
            />
            <div className={styles.lightboxDim} />

            {/* Скрытый preloader — грузит следующее фото */}
            {incomingPhoto && (
              <img
                key={`preload-${incomingPhoto.id}`}
                src={incomingPhoto.url}
                style={{ display: 'none' }}
                onLoad={() => {
                  setDisplayedPhoto(incomingPhoto);
                  setIncomingPhoto(null);
                  setAnimKey(k => k + 1);
                }}
                onError={() => {
                  setDisplayedPhoto(incomingPhoto);
                  setIncomingPhoto(null);
                  setPreviewBroken(true);
                  setAnimKey(k => k + 1);
                }}
              />
            )}

            {/* Кнопка закрыть */}
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={() => setDisplayedPhoto(null)}
            >
              <CloseIcon className={styles.lightboxCloseIcon} />
            </button>

            {/* Контент */}
            <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
              <button
                type="button"
                className={`${styles.lightboxNav} ${incomingPhoto ? styles.lightboxNavLoading : ''}`}
                onClick={() => navigate(-1)}
              >
                <ArrowLeftIcon className={styles.lightboxNavIcon} />
              </button>

              <div className={styles.lightboxImageWrapper}>
                {!previewBroken ? (
                  <img
                    key={animKey}
                    src={displayedPhoto.url}
                    alt=""
                    className={`${styles.lightboxImage} ${navDir === 'right' ? styles.slideFromRight : styles.slideFromLeft}`}
                  />
                ) : (
                  <div className={styles.lightboxBroken}>
                    <EmptyImageIcon className={styles.lightboxBrokenIcon} />
                    <span className={styles.lightboxBrokenText}>{t('failed_to_load')}</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                className={`${styles.lightboxNav} ${incomingPhoto ? styles.lightboxNavLoading : ''}`}
                onClick={() => navigate(1)}
              >
                <ArrowRightIcon className={styles.lightboxNavIcon} />
              </button>
            </div>

            {/* Счётчик */}
            <div className={styles.lightboxCounter}>
              {incomingPhoto && <span className={styles.lightboxSpinner} />}
              {photoList.findIndex(p => p.id === displayedPhoto.id) + 1} / {photoList.length}
            </div>
          </div>
        </Portal>
      )}

      {/* Подтверждение удаления */}
      <Modal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <div className={styles.confirmWrapper}>
          <p className={styles.confirmTitle}>{t('sure_delete_file')}</p>
          <p className={styles.confirmDescription}>
            {t('photo_will_be_deleted_everywhere')}
          </p>
          <div className={styles.confirmButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setDeleteConfirmId(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className={styles.confirmDeleteButton}
              onClick={() => deleteConfirmId && deletePhoto(deleteConfirmId)}
            >
              {t('delete')}
            </button>
          </div>
        </div>
      </Modal>
    </Page>
  );
};

export default PhotoStoragePhotos;
